import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { apiFetch } from '@/lib/api-client'

interface Territory {
  id: string
  name: string
  kind: string
  isoCode: string | null
}

const SELECT_CLASS =
  'w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none'

function useCountries() {
  return useQuery<Territory[]>({
    queryKey: ['territories', 'countries'],
    queryFn: () => apiFetch<Territory[]>('/territories?kind=country'),
    staleTime: Infinity,
  })
}

function sortCountries(countries: Territory[]) {
  return [...countries].sort((a, b) => a.name.localeCompare(b.name))
}

export function CountrySelect({
  id,
  label,
  value,
  onChange,
  required,
  placeholder = 'Select country…',
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  placeholder?: string
}) {
  const { data: countries = [], isLoading } = useCountries()
  const sorted = useMemo(() => sortCountries(countries), [countries])
  const valueInList = !value || sorted.some((c) => c.name === value)

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={isLoading}
        className={SELECT_CLASS}
      >
        <option value="">{isLoading ? 'Loading…' : placeholder}</option>
        {!valueInList && value && <option value={value}>{value}</option>}
        {sorted.map((c) => (
          <option key={c.id} value={c.name}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  )
}

export function CountryMultiSelect({
  id,
  label,
  values,
  onChange,
  hint,
  addOnSelect = false,
  required = false,
  error,
}: {
  id: string
  label: string
  values: string[]
  onChange: (values: string[]) => void
  hint?: string
  /** When true, choosing a country in the dropdown adds it immediately. */
  addOnSelect?: boolean
  required?: boolean
  error?: string | null
}) {
  const [pending, setPending] = useState('')
  const { data: countries = [], isLoading } = useCountries()
  const sorted = useMemo(() => sortCountries(countries), [countries])
  const available = sorted.filter((c) => !values.includes(c.name))

  function addCountry(name: string) {
    if (name && !values.includes(name)) onChange([...values, name])
    setPending('')
  }

  function removeCountry(name: string) {
    onChange(values.filter((v) => v !== name))
  }

  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </label>
      {values.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full border border-ovation-ink/10 bg-ovation-ink/5 px-2 py-0.5 text-xs"
            >
              {v}
              <button
                type="button"
                onClick={() => removeCountry(v)}
                className="rounded p-0.5 text-ovation-muted hover:text-ovation-ink"
                aria-label={`Remove ${v}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className={addOnSelect ? undefined : 'flex gap-2'}>
        <select
          id={id}
          value={pending}
          onChange={(e) => {
            const name = e.target.value
            if (addOnSelect && name) {
              addCountry(name)
            } else {
              setPending(name)
            }
          }}
          disabled={isLoading || available.length === 0}
          required={required && values.length === 0}
          className={SELECT_CLASS}
        >
          <option value="">
            {isLoading
              ? 'Loading…'
              : available.length === 0
                ? values.length > 0
                  ? 'All countries added'
                  : 'No countries available'
                : addOnSelect
                  ? 'Select a country…'
                  : 'Add country…'}
          </option>
          {available.map((c) => (
            <option key={c.id} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        {!addOnSelect && (
          <button
            type="button"
            disabled={!pending}
            onClick={() => addCountry(pending)}
            className="shrink-0 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm hover:bg-ovation-ink/5 disabled:opacity-50"
          >
            Add
          </button>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-ovation-muted">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}
